import {
  Box,
  BoxProps,
  Button,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useDisclosure,
} from 'design-system';
import { SupplierProfileDto, useUpdateSupplierProfileApi } from 'apis/supplier-profiles';
import { useGetCountriesApi } from 'apis/users/use-get-countries-api';
import { isoToTimeInput, timeToIsoString } from 'pages/supplier-profiles/utils/format-cut-off-time';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

type TInputs = {
  name: string;
  countryCode: string;
  email: string;
  imageUrl: string;
  cutOffTime: string;
  minimumOrder: string;
  isActive: boolean;
  isPublic: boolean;
  businessName: string;
  category: string;
  moq: string;
  delivery: string;
  paymentTerms: string;
  commissionRate: string;
  billingLegalName: string;
  billingAddress: string;
  taxRegistrationNo: string;
};

const defaultValues: TInputs = {
  name: '',
  countryCode: '',
  email: '',
  imageUrl: '',
  cutOffTime: '',
  minimumOrder: '',
  isActive: true,
  isPublic: false,
  businessName: '',
  category: '',
  moq: '',
  delivery: '',
  paymentTerms: '',
  commissionRate: '',
  billingLegalName: '',
  billingAddress: '',
  taxRegistrationNo: '',
};

// Same section-header idiom as the reconciliation panels (xs uppercase gray + hairline rule) —
// the rule carries the grouping so the fields below can stay quiet.
const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <HStack spacing='sm' align='center'>
    <Text
      fontSize='xs'
      fontWeight='semibold'
      color='gray.500'
      textTransform='uppercase'
      letterSpacing='0.4px'
      whiteSpace='nowrap'>
      {children}
    </Text>
    <Box flex='1' h='1px' bg='gray.100' />
  </HStack>
);

// Spans both grid columns on desktop — for fields whose real content is long
// (multi-emirate MOQ notes, delivery terms, street addresses, URLs).
const fullRow = { base: 'auto', md: '1 / -1' };

export interface IEditSupplierProfileModalCell extends BoxProps {
  trigger: JSX.Element;
  profile: SupplierProfileDto;
}

export const EditSupplierProfileModal = ({ trigger, profile }: IEditSupplierProfileModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isLinked = !!profile.supplierId;

  const { data: countriesData } = useGetCountriesApi({ enabled: isOpen && isLinked });
  const { mutate: updateSupplierProfile, isLoading } = useUpdateSupplierProfileApi();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const isActiveValue = useWatch({ control, name: 'isActive' });
  const isPublicValue = useWatch({ control, name: 'isPublic' });

  const countryOptions = useMemo(
    () =>
      (countriesData ?? []).map((country: any) => ({
        value: country?.code,
        label: country?.name ?? country?.code,
      })),
    [countriesData]
  );

  useEffect(() => {
    if (!isOpen) return;

    reset({
      name: profile.supplier?.name ?? '',
      countryCode: profile.supplier?.countryCode ?? '',
      email: profile.supplier?.email ?? '',
      imageUrl: profile.supplier?.imageUrl ?? '',
      cutOffTime: isoToTimeInput(profile.supplier?.cutOffTime),
      minimumOrder: profile.supplier?.minimumOrder != null ? String(profile.supplier.minimumOrder) : '',
      isActive: profile.supplier?.isActive ?? true,
      isPublic: profile.supplier?.isPublic ?? false,
      businessName: profile.businessName ?? '',
      category: profile.category ?? '',
      moq: profile.moq ?? '',
      delivery: profile.delivery ?? '',
      paymentTerms: profile.paymentTerms ?? '',
      commissionRate: profile.commissionRate != null ? String(profile.commissionRate) : '',
      billingLegalName: profile.billingLegalName ?? '',
      billingAddress: profile.billingAddress ?? '',
      taxRegistrationNo: profile.taxRegistrationNo ?? '',
    });
  }, [isOpen, profile, countriesData]);

  const handleClose = () => {
    reset({ ...defaultValues });
    onClose();
  };

  const onSubmit = (values: TInputs) => {
    updateSupplierProfile(
      {
        id: profile.id,
        body: {
          ...(isLinked
            ? {
                supplier: {
                  name: values.name.trim(),
                  countryCode: values.countryCode,
                  email: values.email.trim() || null,
                  imageUrl: values.imageUrl.trim() || null,
                  cutOffTime: values.cutOffTime ? timeToIsoString(values.cutOffTime) : null,
                  minimumOrder: values.minimumOrder ? Number(values.minimumOrder) : null,
                  isActive: values.isActive,
                  isPublic: values.isPublic,
                },
              }
            : {}),
          profile: {
            businessName: values.businessName.trim(),
            category: values.category.trim(),
            moq: values.moq.trim(),
            delivery: values.delivery.trim(),
            paymentTerms: values.paymentTerms.trim(),
            commissionRate: values.commissionRate ? Number(values.commissionRate) : null,
            // Omit-if-empty until the list endpoint returns billing fields (BE follow-up):
            // sending '' would silently wipe values set elsewhere. Trade-off: cannot clear
            // a value from this form yet.
            ...(values.billingLegalName.trim() ? { billingLegalName: values.billingLegalName.trim() } : {}),
            ...(values.billingAddress.trim() ? { billingAddress: values.billingAddress.trim() } : {}),
            ...(values.taxRegistrationNo.trim() ? { taxRegistrationNo: values.taxRegistrationNo.trim() } : {}),
          },
        },
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      {/* scrollBehavior='inside' pins the header and footer — Save stays reachable while the
          field area scrolls; 3xl + the two-column grids keep that scroll short. */}
      <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: '3xl' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Edit Supplier Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb='md'>
            <Stack spacing='lg'>
              {isLinked && (
                <Stack spacing='md'>
                  <SectionHeading>Supplier details</SectionHeading>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacingX='lg' spacingY='md'>
                    <FormControl isRequired isInvalid={!!errors.name}>
                      <FormLabel>Supplier name</FormLabel>
                      <Input
                        {...register('name', { validate: (value) => value.trim().length > 0 || 'Supplier name is required' })}
                      />
                      <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                    </FormControl>

                    <Controller
                      control={control}
                      name='countryCode'
                      rules={{ required: 'Country is required' }}
                      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <FormControl isRequired isInvalid={!!error}>
                          <FormLabel>Country</FormLabel>
                          <ChakraSelect
                            options={countryOptions}
                            value={countryOptions.find((option) => option.value === value) ?? null}
                            onChange={(option: any) => onChange(option?.value ?? '')}
                            onBlur={onBlur}
                            placeholder='Select country'
                            isClearable
                          />
                          <FormErrorMessage>{error?.message}</FormErrorMessage>
                        </FormControl>
                      )}
                    />

                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type='email'
                        placeholder='e.g. orders@freshfoods.ae'
                        {...register('email', {
                          validate: (value) =>
                            !value.trim() || EMAIL_PATTERN.test(value.trim()) || 'Enter a valid email address',
                        })}
                      />
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Image URL</FormLabel>
                      <Input placeholder='https://...' {...register('imageUrl')} />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Cut-off time</FormLabel>
                      <Input type='time' {...register('cutOffTime')} />
                    </FormControl>

                    <FormControl isInvalid={!!errors.minimumOrder}>
                      <FormLabel>Min. order value</FormLabel>
                      <Input
                        type='number'
                        step='any'
                        min='0'
                        {...register('minimumOrder', {
                          validate: (value) => !value || Number(value) > 0 || 'Minimum order value must be greater than 0',
                        })}
                      />
                      <FormErrorMessage>{errors.minimumOrder?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={{ base: 'xs', sm: 'xl' }}>
                    <FormControl display='flex' alignItems='center' minH='40px' w='auto'>
                      <Switch
                        id='edit-is-active'
                        isChecked={!!isActiveValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isActive', e.target.checked)}
                        colorScheme='red'
                        me='sm'
                      />
                      <FormLabel htmlFor='edit-is-active' mb='0' cursor='pointer'>
                        Active
                      </FormLabel>
                    </FormControl>

                    <FormControl display='flex' alignItems='center' minH='40px' w='auto'>
                      <Switch
                        id='edit-is-public'
                        isChecked={!!isPublicValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isPublic', e.target.checked)}
                        colorScheme='red'
                        me='sm'
                      />
                      <FormLabel htmlFor='edit-is-public' mb='0' cursor='pointer'>
                        Public
                      </FormLabel>
                    </FormControl>
                  </Stack>
                </Stack>
              )}

              <Stack spacing='md'>
                <SectionHeading>Profile details</SectionHeading>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX='lg' spacingY='md'>
                  <FormControl isRequired isInvalid={!!errors.businessName}>
                    <FormLabel>Business name</FormLabel>
                    <Input
                      {...register('businessName', {
                        validate: (value) => value.trim().length > 0 || 'Business name is required',
                      })}
                    />
                    <FormErrorMessage>{errors.businessName?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Category</FormLabel>
                    <Input {...register('category')} />
                  </FormControl>

                  <FormControl gridColumn={fullRow}>
                    <FormLabel>MOQ</FormLabel>
                    <Textarea rows={2} {...register('moq')} />
                  </FormControl>

                  <FormControl gridColumn={fullRow}>
                    <FormLabel>Delivery</FormLabel>
                    <Textarea rows={2} {...register('delivery')} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Payment terms</FormLabel>
                    <Input {...register('paymentTerms')} />
                  </FormControl>

                  <FormControl isInvalid={!!errors.commissionRate}>
                    <FormLabel>Commission %</FormLabel>
                    <Input
                      type='number'
                      step='0.01'
                      min='0'
                      max='100'
                      {...register('commissionRate', {
                        validate: (value) =>
                          !value || (Number(value) >= 0 && Number(value) <= 100) || 'Commission % must be between 0 and 100',
                      })}
                    />
                    <FormErrorMessage>{errors.commissionRate?.message}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </Stack>

              <Stack spacing='md'>
                <SectionHeading>Billing details</SectionHeading>
                <Text fontSize='xs' color='gray.400' mt='-6px'>
                  Shown in the Bill To block of generated proforma invoices.
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX='lg' spacingY='md'>
                  <FormControl>
                    <FormLabel>Billing legal name</FormLabel>
                    <Input {...register('billingLegalName')} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tax registration no. (TRN)</FormLabel>
                    <Input {...register('taxRegistrationNo')} />
                  </FormControl>

                  <FormControl gridColumn={fullRow}>
                    <FormLabel>Billing address</FormLabel>
                    <Input {...register('billingAddress')} />
                  </FormControl>
                </SimpleGrid>
              </Stack>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button variant='outline' colorScheme='red' onClick={handleClose}>
                Cancel
              </Button>
              <Button variant='solid' colorScheme='red' type='submit' isLoading={isLoading}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
